/**
 * Hook: rank Industry News RSS articles via Trace API vector similarity.
 * Ranks progressively so the list re-sorts as score batches arrive.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  rankIndustryNewsArticlesProgressive,
  DEFAULT_NEWS_SIMILARITY_THRESHOLD,
} from '../api/industryNews';
import { RSSFeedItem } from '../services/usGovernmentIntegrations';
import {
  attachRelevanceScores,
  buildScoreMap,
  countAboveSimilarityThreshold,
  IndustryNewsSortMode,
  mergeScoreMaps,
  rankAndFilterIndustryNewsWithPending,
  ScoredNewsArticle,
} from '../utils/industryNewsRanking';

export interface UseIndustryNewsRankingOptions {
  projectId?: string;
  articles: RSSFeedItem[];
  keywords: string[];
  sortMode?: IndustryNewsSortMode;
  similarityThreshold?: number;
  /** When true, hard-filters below-threshold articles (legacy). Default false — paginate full list. */
  applyThreshold?: boolean;
  enabled?: boolean;
}

export function useIndustryNewsRanking({
  projectId,
  articles,
  keywords,
  sortMode = 'relevance',
  similarityThreshold = DEFAULT_NEWS_SIMILARITY_THRESHOLD,
  applyThreshold = false,
  enabled = true,
}: UseIndustryNewsRankingOptions) {
  const keywordKey = useMemo(() => keywords.join('|'), [keywords]);
  const rankingContextKey = `${keywordKey}|${similarityThreshold}`;

  const rankingEnabled =
    enabled && !!projectId && articles.length > 0 && keywords.length > 0;

  const [scoreMap, setScoreMap] = useState<
    Map<string, { score: number; matchedKeywords: string[] }>
  >(() => new Map());
  const [pendingArticleIds, setPendingArticleIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [isRanking, setIsRanking] = useState(false);
  const [rankingUnavailable, setRankingUnavailable] = useState(false);
  const [rankingError, setRankingError] = useState<unknown>(null);
  const rankingGenerationRef = useRef(0);
  const rankingContextRef = useRef(rankingContextKey);
  const rankedArticleIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!rankingEnabled || !projectId) {
      setScoreMap(new Map());
      setPendingArticleIds(new Set());
      setIsRanking(false);
      setRankingUnavailable(false);
      setRankingError(null);
      rankedArticleIdsRef.current = new Set();
      rankingContextRef.current = rankingContextKey;
      return;
    }

    const contextChanged = rankingContextRef.current !== rankingContextKey;
    if (contextChanged) {
      rankingContextRef.current = rankingContextKey;
      rankedArticleIdsRef.current = new Set();
      setScoreMap(new Map());
      setPendingArticleIds(new Set());
    }

    const articlesToRank = contextChanged
      ? articles
      : articles.filter((article) => !rankedArticleIdsRef.current.has(article.id));

    if (articlesToRank.length === 0) {
      return;
    }

    const generation = rankingGenerationRef.current + 1;
    rankingGenerationRef.current = generation;

    setPendingArticleIds((previous) => {
      const next = contextChanged ? new Set<string>() : new Set(previous);
      for (const article of articlesToRank) {
        next.add(article.id);
      }
      return next;
    });
    setIsRanking(true);
    setRankingUnavailable(false);
    setRankingError(null);

    void rankIndustryNewsArticlesProgressive(
      projectId,
      keywords,
      articlesToRank.map((article) => ({
        id: article.id,
        title: article.title,
        description: article.description,
      })),
      similarityThreshold,
      (partialResults) => {
        if (rankingGenerationRef.current !== generation) {
          return;
        }

        const partialMap = buildScoreMap(partialResults);
        setScoreMap((previous) => mergeScoreMaps(previous, partialMap));
        setPendingArticleIds((previous) => {
          const next = new Set(previous);
          for (const row of partialResults) {
            next.delete(row.article_id);
          }
          return next;
        });
      }
    )
      .then((response) => {
        if (rankingGenerationRef.current !== generation) {
          return;
        }

        for (const article of articlesToRank) {
          rankedArticleIdsRef.current.add(article.id);
        }

        if (response === null) {
          setRankingUnavailable(true);
        } else {
          setScoreMap((previous) =>
            mergeScoreMaps(previous, buildScoreMap(response.results))
          );
        }

        setPendingArticleIds((previous) => {
          const next = new Set(previous);
          for (const article of articlesToRank) {
            next.delete(article.id);
          }
          return next;
        });
        setIsRanking(false);
      })
      .catch((error) => {
        if (rankingGenerationRef.current !== generation) {
          return;
        }
        console.warn(
          '[useIndustryNewsRanking] Ranking unavailable, falling back to date sort:',
          error
        );
        setRankingUnavailable(true);
        setScoreMap(new Map());
        setPendingArticleIds(new Set());
        setRankingError(error);
        setIsRanking(false);
        rankedArticleIdsRef.current = new Set();
      });
  }, [
    articles,
    keywordKey,
    keywords,
    projectId,
    rankingContextKey,
    rankingEnabled,
    similarityThreshold,
  ]);

  const rankedArticles: ScoredNewsArticle[] = useMemo(() => {
    const sortArticles = (mode: IndustryNewsSortMode) =>
      rankAndFilterIndustryNewsWithPending(articles, scoreMap, pendingArticleIds, {
        sortMode: mode,
        threshold: similarityThreshold,
        applyThreshold: false,
        prioritizeAboveThreshold: false,
      });

    if (keywords.length === 0) {
      return sortArticles('date');
    }

    const ranked = sortArticles(sortMode);

    if (applyThreshold) {
      const filtered = rankAndFilterIndustryNewsWithPending(
        articles,
        scoreMap,
        pendingArticleIds,
        {
          sortMode,
          threshold: similarityThreshold,
          applyThreshold: true,
          prioritizeAboveThreshold: false,
        }
      );
      if (filtered.length === 0 && articles.length > 0) {
        return sortArticles('date');
      }
      return filtered;
    }

    if (
      rankingUnavailable &&
      ranked.every(
        (article) => !article.relevanceScorePending && article.relevanceScore === 0
      )
    ) {
      return sortArticles(sortMode === 'relevance' ? 'date' : sortMode);
    }

    return ranked;
  }, [
    applyThreshold,
    articles,
    keywords.length,
    pendingArticleIds,
    rankingUnavailable,
    scoreMap,
    similarityThreshold,
    sortMode,
  ]);

  const aboveThresholdCount = useMemo(() => {
    if (keywords.length === 0) {
      return 0;
    }
    const scored = attachRelevanceScores(articles, scoreMap, pendingArticleIds);
    return countAboveSimilarityThreshold(scored, similarityThreshold);
  }, [articles, keywords.length, pendingArticleIds, scoreMap, similarityThreshold]);

  const totalRanked = rankingUnavailable ? articles.length : articles.length;

  return {
    rankedArticles,
    scoreMap,
    isRanking,
    rankingUnavailable,
    rankingError,
    aboveThresholdCount,
    totalRanked,
    refetchRanking: () => {
      rankingGenerationRef.current += 1;
      rankedArticleIdsRef.current = new Set();
    },
  };
}
